import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Typography,
  TextField,
  Paper,
  Stack,
  Box,
  Alert,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { AppDispatch, RootState } from "../../app/store";
import {
  inputTokenChanged,
  decodeJwtAsync,
  clearDecoded,
  clearAll,
} from "./JwtDecoderSlice";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { HomeCard } from "../../components/home_card/HomeCard";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import { useLocation } from "react-router-dom";
import {
  getLanguageUrl,
  getLanguageFromUrl,
} from "../../utilities/languageUrls";
import Base64EncoderImage from "../../assets/Base64Encoder.webp";
import CaseConverterImage from "../../assets/CaseConverter.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

const JwtDecoder: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const { inputToken, decodedHeader, decodedPayload, isDecoding, error } =
    useSelector((state: RootState) => state.jwtDecoder);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(inputTokenChanged(event.target.value));
    },
    [dispatch]
  );

  const handleDecode = useCallback(() => {
    if (inputToken.trim()) {
      dispatch(decodeJwtAsync(inputToken.trim()));
    }
  }, [dispatch, inputToken]);

  const handleClearDecoded = useCallback(() => {
    dispatch(clearDecoded());
  }, [dispatch]);

  const handleClearAll = useCallback(() => {
    dispatch(clearAll());
  }, [dispatch]);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  }, []);

  return (
    <SideMenuPageContainer
      pageTitle={t("jwtDecoder.title")}
      pageSeoTitle={t("jwtDecoder.seoTitle")}
      pageDescription={t("jwtDecoder.description")}
    >
      <Grid2
        container
        spacing={2}
        sx={{ mt: 2 }}
        columns={{ xs: 6, sm: 6, md: 12 }}
      >
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("jwtDecoder.mainHeading")}
          </Typography>
        </Grid2>

        {/* Sample JWT Token */}
        <Grid2 xs={12}>
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" sx={{ color: "white" }}>
                Sample JWT Token
              </Typography>
              <Tooltip title="Copy sample token to clipboard">
                <IconButton
                  onClick={() =>
                    copyToClipboard(
                      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6Ik51bWJlckNob21wZXIgVXNlciIsImlhdCI6MTUxNjIzOTAyMiwiZXhwIjoxNzM0NTY3ODkwLCJpc3MiOiJOdW1iZXJDaG9tcGVyLmNvbSIsImF1ZCI6Ik51bWJlckNob21wZXIgVG9vbHMiLCJyb2xlIjoiZGV2ZWxvcGVyIiwiZmVhdHVyZXMiOlsiand0LWRlY29kZXIiLCJiYXNlNjQtZW5jb2RlciIsInV1aWQtZ2VuZXJhdG9yIl19.K8_vKzP4c6Y2QzJ4aHc8wWqX7rEp9mL5nD3fG1bH6sA"
                    )
                  }
                  size="small"
                  sx={{ color: "white" }}
                >
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6Ik51bWJlckNob21wZXIgVXNlciIsImlhdCI6MTUxNjIzOTAyMiwiZXhwIjoxNzM0NTY3ODkwLCJpc3MiOiJOdW1iZXJDaG9tcGVyLmNvbSIsImF1ZCI6Ik51bWJlckNob21wZXIgVG9vbHMiLCJyb2xlIjoiZGV2ZWxvcGVyIiwiZmVhdHVyZXMiOlsiand0LWRlY29kZXIiLCJiYXNlNjQtZW5jb2RlciIsInV1aWQtZ2VuZXJhdG9yIl19.K8_vKzP4c6Y2QzJ4aHc8wWqX7rEp9mL5nD3fG1bH6sA"
              InputProps={{
                readOnly: true,
                style: {
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                },
              }}
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" sx={{ color: "white" }}>
              Try decoding this sample token to see how it works! It contains
              information about NumberChomper tools and features.
            </Typography>
          </Paper>
        </Grid2>

        {/* Input Section */}
        <Grid2 xs={12}>
          <TextField
            fullWidth
            multiline
            rows={8}
            label={t("jwtDecoder.inputTokenLabel")}
            placeholder={t("jwtDecoder.inputPlaceholder")}
            value={inputToken}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            InputProps={{
              style: {
                fontFamily: "monospace",
                fontSize: "0.75rem",
              },
            }}
          />

          <Stack
            direction="row"
            spacing={2}
            sx={{ mb: 2, justifyContent: "center" }}
          >
            <CustomButton
              label={t("jwtDecoder.decodeButton")}
              onClick={handleDecode}
              disabled={!inputToken.trim() || isDecoding}
              width={150}
            />
            <CustomButton
              label={t("jwtDecoder.clearButton")}
              onClick={handleClearDecoded}
              disabled={!decodedHeader && !decodedPayload}
              width={120}
            />
            <CustomButton
              label={t("jwtDecoder.clearAll")}
              onClick={handleClearAll}
              disabled={!inputToken && !decodedHeader && !decodedPayload}
              width={160}
            />
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Grid2>

        {/* Output Section */}
        <Grid2 xs={12}>
          <Stack spacing={3}>
            {/* Header */}
            {decodedHeader && (
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  backgroundColor: "#404f65",
                  borderRadius: "10px",
                  boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "translateY(-3px) scale(1.015)",
                    boxShadow: "0 8px 30px #f7ef912a",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "#c1edff", fontSize: "20px" }}
                  >
                    {t("jwtDecoder.decodedHeaderLabel")}
                  </Typography>
                  <Tooltip title={t("jwtDecoder.copyTooltip")}>
                    <IconButton
                      onClick={() => copyToClipboard(decodedHeader)}
                      size="small"
                      sx={{ color: "#98c8db" }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={decodedHeader}
                  InputProps={{
                    readOnly: true,
                    style: {
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      color: "#c1edff",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#98c8db",
                      },
                      "&:hover fieldset": {
                        borderColor: "#c1edff",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#c1edff",
                      },
                    },
                  }}
                />
              </Paper>
            )}

            {/* Payload */}
            {decodedPayload && (
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  backgroundColor: "#404f65",
                  borderRadius: "10px",
                  boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
                  transition: "0.3s",
                  "&:hover": {
                    transform: "translateY(-3px) scale(1.015)",
                    boxShadow: "0 8px 30px #f7ef912a",
                  },
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "#c1edff", fontSize: "20px" }}
                  >
                    {t("jwtDecoder.decodedPayloadLabel")}
                  </Typography>
                  <Tooltip title={t("jwtDecoder.copyTooltip")}>
                    <IconButton
                      onClick={() => copyToClipboard(decodedPayload)}
                      size="small"
                      sx={{ color: "#98c8db" }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  variant="outlined"
                  value={decodedPayload}
                  InputProps={{
                    readOnly: true,
                    style: {
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      color: "#c1edff",
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#98c8db",
                      },
                      "&:hover fieldset": {
                        borderColor: "#c1edff",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#c1edff",
                      },
                    },
                  }}
                />
              </Paper>
            )}
          </Stack>
        </Grid2>

        {/* Divider */}
        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>

        {/* SEO Content Section */}
        <Grid2 xs={12}>
          <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("jwtDecoder.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("jwtDecoder.content.freeDescription")}
          </Typography>

          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("jwtDecoder.content.whatIsHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("jwtDecoder.content.whatIsDescription")}
          </Typography>

          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("jwtDecoder.content.useCasesHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("jwtDecoder.content.useCasesDescription")}
          </Typography>

          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("jwtDecoder.content.howToHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("jwtDecoder.content.howToDescription")}
          </Typography>
        </Grid2>

        {/* Related Tools Section */}
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("jwtDecoder.relatedTools")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="base64Encoder"
            link={getLanguageUrl(
              getLanguageFromUrl(location.pathname),
              "base64Encoder"
            )}
            smallCard={true}
            image={Base64EncoderImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="caseConverter"
            link={getLanguageUrl(
              getLanguageFromUrl(location.pathname),
              "caseConverter"
            )}
            smallCard={true}
            image={CaseConverterImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
};

export default JwtDecoder;
